/**
 * Salvataggio di un artefatto di sessione (collettivo o individuale): UN'unica implementazione per tutte le app
 * (plan docs/plans/2026-10-29-artefatti-collettivi-helper-condiviso.md, WP5). Identica in standalone e in
 * TechyWeb: cambia solo da dove l'app prende `sessionId` (sessione dell'app, mai la radice).
 *
 * Errori del server (`{ error, code }`) -> `ArtifactSaveError` con messaggio italiano per l'utente: niente
 * fallimenti silenziosi, niente alert/confirm (l'app mostra `err.message` nel suo toast/errore).
 */

import { COLLAB_API_BASE, authFetch, collabFetch } from "../auth/authService";

export type ArtifactScope = "collective" | "individual";

/**
 * Docente => collettivo; studente => individuale. Un'app che ammette la scrittura collettiva dello studente
 * (oggi solo Disegna, workMode 'shared') passa `sharedWorkMode: true`.
 */
export function artifactScopeFor(opts: { isTeacher: boolean; sharedWorkMode?: boolean }): ArtifactScope {
  return opts.isTeacher || opts.sharedWorkMode === true ? "collective" : "individual";
}

export class ArtifactSaveError extends Error {
  /** Stato HTTP (0 = nessuna risposta: rete o accesso mancante). */
  readonly status: number;
  /** Codice del server (`session_not_active`, ...) o locale (`network`, `no_auth`, `http_<stato>`). */
  readonly code: string;
  /** Messaggio grezzo del server, per i log. */
  readonly serverMessage: string | null;
  /** Corpo JSON della risposta d'errore (es. `current` nel conflitto di mtime di Disegna), se presente. */
  readonly body: Record<string, any> | null;
  constructor(
    status: number,
    code: string,
    message: string,
    serverMessage: string | null = null,
    body: Record<string, any> | null = null,
  ) {
    super(message);
    this.name = "ArtifactSaveError";
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
    this.body = body;
  }
}

const CLOSED = "L'incontro è chiuso: il lavoro di classe non si può più salvare.";
const NOT_FOUND = "Sessione non trovata: riapri l'attività dall'incontro.";
const SIGN_IN = "Accesso scaduto: accedi di nuovo per salvare.";

/** Messaggio italiano per l'utente, dal codice del server e dallo stato HTTP. */
export function artifactSaveErrorMessage(status: number, code: string | null, scope: ArtifactScope = "collective"): string {
  switch (code) {
    case "session_not_active":
      return CLOSED;
    case "session_not_found":
      return NOT_FOUND;
    case "wrong_session_type":
      return "Questa sessione non è di questa attività: riapri l'attività dall'incontro.";
    case "no_auth":
      return SIGN_IN;
    case "network":
      return "Connessione assente: il salvataggio non è riuscito. Riprova tra poco.";
    default:
      break;
  }
  if (status === 401) return SIGN_IN;
  if (status === 403) {
    return scope === "collective"
      ? "Solo il docente della sessione può salvare il lavoro di classe."
      : "Non hai il permesso di salvare in questa sessione.";
  }
  if (status === 404) return NOT_FOUND;
  if (status === 409) return "Il lavoro è stato modificato nel frattempo: ricarica e riprova.";
  if (status === 413) return "Il lavoro è troppo grande per essere salvato.";
  if (status === 429) return "Troppi salvataggi ravvicinati: aspetta qualche secondo e riprova.";
  return "Salvataggio non riuscito. Riprova tra poco.";
}

export interface SaveSessionArtifactOptions {
  /** Id dell'app nella rotta `/artifacts/<app>` (es. 'wordcloud', 'pixel-art'). */
  app: string;
  /** Sessione DELL'APP (figlia derivata o standalone), mai la radice. */
  sessionId: string;
  scope: ArtifactScope;
  /** Resto del corpo (metadata, project_data, local_project_id, pin, pseudonym_identity_id...). `session_id` e `type` li imposta l'helper. */
  body: Record<string, unknown>;
  /** Percorso sotto la base se non è `/artifacts/<app>` (es. `/artifacts/disegna/save`). */
  path?: string;
  /** Metodo HTTP (default POST). */
  method?: "POST" | "PUT" | "PATCH";
  /** Header aggiuntivi (es. `X-TL4K-App`). */
  headers?: Record<string, string>;
  /** Base URL (default `COLLAB_API_BASE`). */
  baseUrl?: string;
  /**
   * Chi firma la richiesta. Default: 'jwt' (docente, `authFetch`) per il collettivo, 'student' (token studente,
   * `collabFetch`) per l'individuale. Disegna 'shared' dello studente: scope collective + auth 'student'.
   */
  auth?: "jwt" | "student";
  /** Per i test o per fetch speciali; sostituisce la scelta jwt/student. */
  fetchImpl?: (url: string, init: RequestInit) => Promise<Response>;
}

/** Salva e restituisce la riga del server (JSON), oppure lancia `ArtifactSaveError`. */
export async function saveSessionArtifact<T = any>(opts: SaveSessionArtifactOptions): Promise<T> {
  const { app, sessionId, scope, body } = opts;
  const base = (opts.baseUrl ?? COLLAB_API_BASE).replace(/\/$/, "");
  const url = `${base}${opts.path ?? `/artifacts/${app}`}`;
  const auth = opts.auth ?? (scope === "collective" ? "jwt" : "student");
  const doFetch = opts.fetchImpl ?? (auth === "jwt" ? authFetch : collabFetch);

  let res: Response;
  try {
    res = await doFetch(url, {
      method: opts.method ?? "POST",
      headers: { "Content-Type": "application/json", ...opts.headers },
      body: JSON.stringify({ ...body, session_id: sessionId, type: scope }),
    });
  } catch (e) {
    const raw = e instanceof Error ? e.message : String(e);
    const noAuth = /authentication tokens|session expired/i.test(raw);
    const code = noAuth ? "no_auth" : "network";
    throw new ArtifactSaveError(0, code, artifactSaveErrorMessage(0, code, scope), raw);
  }

  if (!res.ok) {
    let code: string | null = null;
    let serverMessage: string | null = null;
    let errBody: Record<string, any> | null = null;
    try {
      const j = await res.json();
      if (j && typeof j === "object") errBody = j;
      if (j && typeof j.code === "string") code = j.code;
      if (j && typeof j.error === "string") serverMessage = j.error;
    } catch {
      // corpo non JSON: resta il solo stato HTTP
    }
    throw new ArtifactSaveError(
      res.status,
      code ?? `http_${res.status}`,
      artifactSaveErrorMessage(res.status, code, scope),
      serverMessage,
      errBody,
    );
  }

  try {
    return (await res.json()) as T;
  } catch {
    // 200/201/204 senza corpo JSON: il salvataggio e' riuscito
    return undefined as T;
  }
}

/** Messaggio da mostrare per qualunque errore di salvataggio (ArtifactSaveError o altro). */
export function artifactSaveUserMessage(e: unknown, fallback = "Salvataggio non riuscito. Riprova tra poco."): string {
  return e instanceof ArtifactSaveError ? e.message : fallback;
}
